import { useMemo, useRef } from 'react'
import { useQueries } from 'react-query'
import { fetchProductById } from '../../services/productServices'
import { retryUnlessUnauthorized } from './foundStockErrors'
import { foundStockQueryKeys } from './foundStockQueryKeys'

const toSummary = product => ({
  id: product.id,
  name: product.name,
  skus: (product.product_codes || []).map(c => c.sku).filter(Boolean),
  storeName: product.store?.name || null,
})

const sameRefs = (a, b) => a.length === b.length && a.every((value, i) => value === b[i])

// The found-stock reads carry product_id only; resolve names client-side, once per distinct id.
export const useProductSummaries = productIds => {
  const ids = useMemo(() => [...new Set((productIds || []).filter(id => id != null))], [productIds])
  // Hoisted out of the useQueries() call: an inline array is a new value on every render, which
  // re-runs react-query's setQueries effect each time.
  const queries = useMemo(
    () =>
      ids.map(id => ({
        queryKey: foundStockQueryKeys.product(id),
        queryFn: () => fetchProductById(id),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: retryUnlessUnauthorized,
      })),
    [ids],
  )
  const results = useQueries(queries)

  // useQueries builds its result array fresh on every render (react-query 3.39), so a useMemo keyed
  // on it never hits and every consumer's row memo rebuilds on each keystroke. Cache the map against
  // the resolved payload references instead (v3 structural sharing keeps those stable), so the
  // returned object only changes identity when a lookup actually resolves.
  const cache = useRef({ ids: [], data: [], map: {} })
  const data = results.map(r => r.data)
  if (!sameRefs(cache.current.ids, ids) || !sameRefs(cache.current.data, data)) {
    const map = {}
    data.forEach((product, i) => {
      if (product) map[ids[i]] = toSummary(product)
    })
    cache.current = { ids, data, map }
  }
  return cache.current.map
}
